import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";
import { terminate, saveNodeType, isHoverForInteractiveType } from "./utils";
import EachFibProjection from "./wrapper/EachFibProjection";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class FibonacciProjection extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawProjection = this.handleDrawProjection.bind(this);

    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);

    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);
    this.handlePoint3Drag = this.handlePoint3Drag.bind(this);

    this.terminate = terminate.bind(this);
    this.getSelectionState =
      isHoverForInteractiveType("projections").bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = [];

    this.state = {
      current: null,
      override: null,
    };
  }

  // Drawing lifecycle - Drag for first 2 points, click for 3rd
  handleDrawProjection(xyValue) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x1)) {
      if (isNotDefined(current.x2) || !current.waitingForThirdPoint) {
        // Dragging to second point (before release)
        this.mouseMoved = true;
        this.setState({
          current: {
            ...current,
            x2: xyValue[0],
            y2: xyValue[1],
          },
        });
      } else if (current.waitingForThirdPoint) {
        // Preview third point after drag is complete
        this.setState({
          current: {
            ...current,
            x3: xyValue[0],
            y3: xyValue[1],
          },
        });
      }
    }
  }

  handleStart(xyValue, moreProps) {
    const { current } = this.state;

    // Only start new drawing if not already drawing
    if (isNotDefined(current) || isNotDefined(current.x1)) {
      this.mouseMoved = false;
      this.setState(
        {
          current: {
            x1: xyValue[0],
            y1: xyValue[1],
            x2: null,
            y2: null,
            x3: null,
            y3: null,
            waitingForThirdPoint: false,
          },
        },
        () => {
          this.props.onStart(moreProps);
        },
      );
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { projections, appearance } = this.props;
    const { current } = this.state;

    if (isNotDefined(current) || isNotDefined(current.x1)) return;

    // If we're still dragging the first line (haven't released yet)
    if (isNotDefined(current.x2) || !this.mouseMoved) {
      // Accidental click without drag - cancel
      this.setState({ current: null });
      return;
    }

    // If we just finished dragging to second point
    if (!current.waitingForThirdPoint) {
      // Finalize second point and wait for third point click
      this.setState({
        current: {
          ...current,
          x2: xyValue[0],
          y2: xyValue[1],
          x3: xyValue[0], // Initialize for preview
          y3: xyValue[1],
          waitingForThirdPoint: true,
        },
      });
      return;
    }

    // Final click - complete the projection
    const newProjection = {
      x1: current.x1,
      y1: current.y1,
      x2: current.x2,
      y2: current.y2,
      x3: xyValue[0],
      y3: xyValue[1],
      selected: true,
      appearance: appearance,
    };

    const newProjections = projections.concat(newProjection);

    this.setState(
      {
        current: null,
      },
      () => {
        this.props.onComplete(newProjections, moreProps, e);
      },
    );
  }

  // Drag handlers
  handleDrag(index, xy) {
    this.setState({
      override: {
        index,
        ...xy,
      },
    });
  }

  handlePoint1Drag(index, newXY) {
    const { projections } = this.props;
    const item = projections[index];

    this.setState({
      override: {
        index,
        x1: newXY[0],
        y1: newXY[1],
        x2: item.x2,
        y2: item.y2,
        x3: item.x3,
        y3: item.y3,
      },
    });
  }

  handlePoint2Drag(index, newXY) {
    const { projections } = this.props;
    const item = projections[index];

    this.setState({
      override: {
        index,
        x1: item.x1,
        y1: item.y1,
        x2: newXY[0],
        y2: newXY[1],
        x3: item.x3,
        y3: item.y3,
      },
    });
  }

  handlePoint3Drag(index, newXY) {
    const { projections } = this.props;
    const item = projections[index];

    this.setState({
      override: {
        index,
        x1: item.x1,
        y1: item.y1,
        x2: item.x2,
        y2: item.y2,
        x3: newXY[0],
        y3: newXY[1],
      },
    });
  }

  handleDragComplete(moreProps) {
    const { projections } = this.props;
    const { override } = this.state;

    if (isDefined(override)) {
      const { index, ...rest } = override;

      const newProjections = projections.map((each, idx) =>
        idx === index ? { ...each, ...rest, selected: true } : each,
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newProjections, moreProps);
        },
      );
    }
  }

  render() {
    const { enabled, projections, appearance, hoverText } = this.props;
    const { current, override } = this.state;

    const hoverTextWithDefaults = {
      ...FibonacciProjection.defaultProps.hoverText,
      ...hoverText,
    };

    const tempProjection =
      isDefined(current) && isDefined(current.x1) ? (
        <EachFibProjection
          interactive={false}
          {...current}
          appearance={appearance}
          hoverText={hoverTextWithDefaults}
        />
      ) : null;

    return (
      <g>
        {projections.map((each, idx) => {
          // Deep merge appearance - instance colors should override base appearance
          // Handle both legacy (string) and new (object) formats
          const eachAppearance = isDefined(each.appearance)
            ? {
                ...appearance,
                ...each.appearance,
                // Handle stroke: can be string '#color' or object {color, width, dash, opacity}
                stroke:
                  typeof each.appearance.stroke === "string"
                    ? { ...appearance.stroke, color: each.appearance.stroke }
                    : typeof each.appearance.stroke === "object"
                      ? { ...appearance.stroke, ...each.appearance.stroke }
                      : appearance.stroke,
                // Handle fill: can be string '#color' or object {color, opacity}
                fill:
                  typeof each.appearance.fill === "string"
                    ? { ...appearance.fill, color: each.appearance.fill }
                    : typeof each.appearance.fill === "object"
                      ? { ...appearance.fill, ...each.appearance.fill }
                      : appearance.fill,
                // Handle font: can be string '#color' or object {fontFamily, fontSize, color}
                font:
                  typeof each.appearance.font === "string"
                    ? { ...appearance.font, color: each.appearance.font }
                    : typeof each.appearance.font === "object"
                      ? { ...appearance.font, ...each.appearance.font }
                      : appearance.font,
              }
            : appearance;

          const props = {
            key: idx,
            ref: this.saveNodeType(idx),
            index: idx,
            ...each,
            appearance: eachAppearance,
            hoverText: hoverTextWithDefaults,

            selected: each.selected,
            hovering: each.hovering,

            onDrag: this.handleDrag,
            onDragComplete: this.handleDragComplete,

            onPoint1Drag: this.handlePoint1Drag.bind(this, idx),
            onPoint2Drag: this.handlePoint2Drag.bind(this, idx),
            onPoint3Drag: this.handlePoint3Drag.bind(this, idx),

            ...(isDefined(override) && override.index === idx ? override : {}),
          };

          return <EachFibProjection {...props} />;
        })}
        {tempProjection}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          shouldDisableSnap={() => true}
          onMouseDown={this.handleStart}
          onMouseMove={this.handleDrawProjection}
          onClick={this.handleEnd}
          r={4}
          stroke={appearance.stroke?.color || appearance.stroke || "#000000"}
          strokeWidth={2}
          opacity={1}
          data-evo-id="interactive-fibonacci-projection"
        />
      </g>
    );
  }
}

FibonacciProjection.propTypes = {
  enabled: PropTypes.bool.isRequired,
  projections: PropTypes.array.isRequired,
  appearance: PropTypes.object.isRequired,

  onStart: PropTypes.func,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,

  hoverText: PropTypes.object.isRequired,
};

FibonacciProjection.defaultProps = {
  projections: [],
  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },
};

export default FibonacciProjection;
